<template>
    <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`"></div>
</template>

<script>
import { round, getDataUrl, getNeutralColors } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import UserService from '../../services/user.service';

  export default {
    name: 'PossessionChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      possession: {
        type: Object,
        required: true,
      },
      game: {
        type: Object,
        required: false
      },
      homeFillColor:{
        type: String,
        default: "#ffffff"
      },
      awayFillColor:{
        type: String,
        default: "#ffffff"
      },
      mediaMode: {
        type: Boolean,
        default: false
      },
      homeBorderColor:{
        type: String,
        default: "#000000"
      },
      awayBorderColor:{
        type: String,
        default: "#000000"
      },
      homeTeamName:{
        type: String,
        default: ""
      },
      awayTeamName:{
        type: String,
        default: ""
      },
      showSequenceNumbers: {
        type: Boolean,
        default: false
      },
      showJerseyNumbers: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        height: 0,
        width: 0,
        images: {}
      }
    },
    computed: {
      SVG_WIDTH(){
        return this.size ? this.size : 380;
      },
      PITCH_WIDTH(){
        if(this.margin){
          return this.SVG_WIDTH - this.margin.left - this.margin.right
        }else{
          return this.SVG_WIDTH;
        }
      },
      PITCH_HEIGHT() {
        return this.game ? this.PITCH_WIDTH * 0.67125 : this.PITCH_WIDTH * 0.755
      },
      rootElementId(){
        return `pitchPossessions${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      }
    },
    async mounted() {
      /* All this events could appear on possessions -> 1,3,7,8,11,12,13,14,15,16,44,49,52,54,60,61,74 (See EventEnum or DIM_EVENT on database) */
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));

      const SVG_WIDTH = this.size ? this.size : 380;
      const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};

      const PITCH_WIDTH = this.PITCH_WIDTH;
      const PITCH_HEIGHT = this.PITCH_HEIGHT;

      d3.selectAll('.tooltip').remove();

      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "tooltip");

      var svg = d3.select(`#${this.rootElementId}`)
        .append("svg")
        .attr("width", SVG_WIDTH)
        .attr("height", PITCH_HEIGHT)
        .style("background-color", "#ffffff")
        .attr("class", "pitch")
        .attr("id", `${this.rootElementId}SVG`);

      // DRAW PITCH
      this.drawImageBase64(svg, margin.left, margin.top, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/pitch_${this.currentUserFieldColor}.png`),'pitch', true);

      let neutralColorsHome = getNeutralColors('home');
      let firstHomeTeamColor = this.homeFillColor;
      let secondHomeTeamColor = neutralColorsHome[1];
      let neutralColorsAway = getNeutralColors('away');
      let firstAwayTeamColor = this.awayFillColor;
      let secondAwayTeamColor = neutralColorsAway[1];
      if (!this.mediaMode) {
        firstHomeTeamColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor : (this.game.homeTeamColor ? this.game.homeTeamColor : firstHomeTeamColor);
        secondHomeTeamColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor : (this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor : secondHomeTeamColor);
        firstAwayTeamColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor : (this.game.awayTeamColor ? this.game.awayTeamColor : firstAwayTeamColor);
        secondAwayTeamColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor : (this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor : secondAwayTeamColor);
      }
      if(firstHomeTeamColor === firstAwayTeamColor){
        firstAwayTeamColor = neutralColorsAway[0];
      }
      if(secondHomeTeamColor === secondAwayTeamColor){
        secondAwayTeamColor = neutralColorsAway[1];
      }

      //If possession has a sequence
      if(this.possession.sequences.length > 0){
        this.renderPossessionStepByStep(svg, PITCH_WIDTH, PITCH_HEIGHT, margin, firstHomeTeamColor, firstAwayTeamColor, this.game, this.possession, tooltip);
      }

      let logo  = require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`);
      await this.drawImageBase64(svg, PITCH_WIDTH * 0.735 + margin.left , PITCH_HEIGHT * 0.77 + margin.top, PITCH_WIDTH * 0.18, PITCH_HEIGHT * 0.18, logo);

      let font = await loadFonts();
      copyAndAppendElement(svg.node(),font.node(),0,0);
    },
    methods: {
      renderPossessionStepByStep(svg, chartWidth, chartHeight, margin, firstHomeTeamColor, firstAwayTeamColor, game, possession, tooltip){
        // ======================================
        //        Render All Events
        // ======================================
        let events = possession.sequences;
        let maxTimeToWait = 8000;//8s
        let duration = events.length > 12 ? maxTimeToWait/events.length : 600;
        for (const [i, event] of events.entries()) {
          let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
          let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);
          let radius = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);
          //By default get current event
          let nextEvent = event;
          let previousEvent = i > 0 ? events[i-1] : event;
          //X and Y Coordinate on SVG of next event
          let sx2;
          let sy2;

          if(i < (events.length - 1)){
            nextEvent = events[i+1];
            //Calculate distance on X axis of end of current event and the begin of the next event
            let xDistanceBetweenEndOfEventAndBeginOfNextEvent = Math.abs(event.originalEndX - nextEvent.originalX);
            //Calculate distance on Y axis of end of current event and the begin of the next event
            let yDistanceBetweenEndOfEventAndBeginOfNextEvent = Math.abs(event.originalEndY - nextEvent.originalY);
            //If there is less than 1m of distance on both axis...
            if(xDistanceBetweenEndOfEventAndBeginOfNextEvent > 0 && xDistanceBetweenEndOfEventAndBeginOfNextEvent <= 1
              && yDistanceBetweenEndOfEventAndBeginOfNextEvent > 0 && yDistanceBetweenEndOfEventAndBeginOfNextEvent <= 1){
              //...then set end of current event at the same point as the start of the next event
              event.originalEndX = nextEvent.originalX;
              event.originalEndY = nextEvent.originalY;
            }
            //Get X and Y from the start of the next event
            sx2 = x(100 - nextEvent.originalX);
            sy2 = y(nextEvent.originalY);

          }else{
            //if there is no next event, get X and Y from the end of the current event
            sx2 = x(100 - event.originalEndX);
            sy2 = y(event.originalEndY);
          }
          //X and Y Coordinate on SVG of current event
          let sx0 = x(100 - event.originalX);
          if (nextEvent.eventTypeId === 16 && event.originalX < 25) // For Own Goals
            sx0 = x(event.originalX);
          let sy0 = y(event.originalY);
          //X and Y Coordinate on SVG of the end of current event
          let sx1 = x(100 - event.originalEndX);
          let sy1 = y(event.originalEndY);

          let nextEventIsAShot = this.isAshot(nextEvent.eventTypeId);
          let nextEventXG = nextEvent.xG > 0.1 ? nextEvent.xG : 0.1;
          //Need to know radius of shot to print triangle on the border
          let radiusValue = nextEventIsAShot ? radius(nextEventXG) : radius(0.02);
          this.drawEvent(svg, i, previousEvent, event, nextEvent, sx0, sy0, sx1, sy1, sx2, sy2, radiusValue, chartWidth, chartHeight, tooltip, duration);
        }

        this.printPossessionHeader(svg, chartWidth, chartHeight, margin, firstHomeTeamColor, firstAwayTeamColor, game, possession);
        this.printLegend(svg, chartWidth, chartHeight, margin, possession, tooltip);

      },
      /*
        sx0 -> Start of the current event on axis X
        sy0 -> Start of the current event on axis Y
        sx1 -> End of the current event on axis X
        sy1 -> End of the current event on axis Y
        sx2 -> Start of the next event on axis X
        sy2 -> Start of the next event on axis Y
      */
      drawEvent(svg, index, previousEvent, event, nextEvent, sx0, sy0, sx1, sy1, sx2, sy2, radius, chartWidth, chartHeight, tooltip, duration) {
        let delay = duration * (index + 1);
        const distanceToNextEvent = this.getDistanceBetweenPoints(sx1, sx2, sy1, sy2);

        //Is continuous line just for Pass(1) or Clearance(12)
        if(this.isContinuousLine(event.eventTypeId)){
          //Draw start of pass line on circle border instead of center of the circle
          const angleRadians = Math.atan2(sy1 - sy0, sx1 - sx0);
          sx0 = sx0 + Math.cos(angleRadians) * radius;
          sy0 = sy0 + Math.sin(angleRadians) * radius;
          sx1 = sx1 - Math.cos(angleRadians) * radius;
          sy1 = sy1 - Math.sin(angleRadians) * radius;
        }
        //Need to avoid carry lines ends inside circles
        const angleRadians2 = Math.atan2(sy2 - sy1, sx2 - sx1);
        sx2 = sx2 - Math.cos(angleRadians2) * radius;
        sy2 = sy2 - Math.sin(angleRadians2) * radius;

        var eventPoints = [
              [sx0, sy0],
              [sx1, sy1]
            ];
        var carryPoints = [
              [sx1, sy1],
              [sx2, sy2]
            ];

        //Draw lines
        this.drawTransitionPassLine(svg, index, previousEvent, event, nextEvent, distanceToNextEvent, eventPoints, carryPoints, sx0, sy0, sx1, sy1, sx2, sy2,
          chartWidth, chartHeight, duration, delay, tooltip);
        //Draw carry dashed lines just if has enough distance between end of current event and start of the next event
        if(distanceToNextEvent > 5){
          this.drawCarryLine(svg, index, event, carryPoints, chartWidth, duration, delay);
        }

      },
      drawCarryLine(svg, index, carry, carryPoints, chartWidth, duration, delay) {
        svg.append("path")
            .datum(carryPoints)
            .attr("d", d3.svg.line())
            .attr('id', "carry-" + index + "-" + carry.teamName)
            .attr("stroke-width", chartWidth * 0.002)
            .attr("stroke-dasharray", this.isAerial(carry.eventTypeId) ? 100 : 3)//When is Aerial need to print continued line instead of dashed line
            .style("opacity", 0)
            .attr("stroke", this.colorScale(1, 0, 0, 100))
            .call(this.transitionCarryLines, duration, delay);
      },
      transitionCarryLines(path, duration, delay) {
        path.transition()
            .duration(duration)
            .delay(delay + duration)//Need to sum duration because need to print at the same time that next event
            .style("opacity", 1);
      },
      drawTransitionPassLine(svg, index, previousEvent, event, nextEvent, distanceToNextEvent, eventPoints, carryPoints, sx0, sy0, sx1, sy1, sx2, sy2,
        chartWidth, chartHeight, duration, delay, tooltip) {

        svg.append("path")
            .datum(eventPoints)
            .attr("d", d3.svg.line())
            .attr('id', "event-" + index + "-" + event.teamName)
            .attr("stroke-width", chartWidth * 0.002)
            .style("opacity", 0)
            //If event is unsuccessful (outcome=false), then print red (0-#fb0e0e), if not print green (1-#28e628)
            .attr("stroke", this.colorScale(!event.outcome ? 0 : 1, 0, 0, 100))
            .call(this.transitionPassLine, svg, index, previousEvent, event, nextEvent, distanceToNextEvent, eventPoints, carryPoints,
              sx0, sy0, sx1, sy1, sx2, sy2, chartWidth, chartHeight, duration, delay, tooltip);
      },
      transitionPassLine(path, svg, index, previousEvent, event, nextEvent, distanceToNextEvent, eventPoints, carryPoints, sx0, sy0, sx1, sy1, sx2, sy2, chartWidth, chartHeight, duration, delay, tooltip) {
        const self = this;
        path.transition()
            .duration(duration)
            .delay(delay)
            .each("end", function() {
              let radius = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);
              let eventIsAShot = self.isAshot(event.eventTypeId);
              let nextEventIsAShot = self.isAshot(nextEvent.eventTypeId);
              let opacity = 0.7;
              let strokeColor = "red";
              let xGShotValue = event.xG > 0.1 ? event.xG : 0.1;
              let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
              let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);

              //Is continuous line just for Pass(1) or Clearance(12)
              if(event.eventTypeId === 1){
                //If is last event and is unsuccessful (outcome=false), then print red (0-#fb0e0e), if not print green (1-#28e628)
                let colorScaleValue = !event.outcome ? 0 : 1;
                strokeColor = self.colorScale(colorScaleValue, 0, 0, 100);
                //Distance between end of the previous event (previousEvent.originalEndX, previousEvent.originalEndY) and begin of the pass (event.originalX, event.originalY)
                const distanceOfPreviousEvent = self.getDistanceBetweenPoints(previousEvent.originalEndX, event.originalX, previousEvent.originalEndY, event.originalY);
                //Distance of the pass
                const distanceOfPass = self.getLengthForPoint(eventPoints[1][0], eventPoints[1][1], this);
                //1.- If previous event is a pass
                //2.- Previous event is not a pass AND distance on X or Y is enough
                if(previousEvent.eventTypeId === 1
                  || (previousEvent.eventTypeId !== 1 && distanceOfPreviousEvent > 3)){

                  const angleRadians = Math.atan2(sy1 - sy0, sx1 - sx0);
                  sx0 = sx0 - Math.cos(angleRadians) * radius(0.02);
                  sy0 = sy0 - Math.sin(angleRadians) * radius(0.02);
                  self.drawCircle(svg, index, chartWidth, sx0, sy0, previousEvent, event, nextEvent, radius(0.02), opacity, strokeColor);
                }
                //1.- Is not a shot and distance of the pass segment is > 15 OR is a shot and there is more than 15 of distance between end of pass and next event
                if((!nextEventIsAShot && distanceOfPass > 20) || (nextEventIsAShot && distanceToNextEvent > 15)){
                  //Draw triangle if current event is a pass and next event is not a shot
                  const angleRadiansTraingle = Math.atan2(sy1 - sy0, sx1 - sx0);
                  sx1 = sx1 - Math.cos(angleRadiansTraingle) * radius(0.02);
                  sy1 = sy1 - Math.sin(angleRadiansTraingle) * radius(0.02);
                  self.drawPassTriangle(svg, index, event, nextEvent, sx0, sy0, sx1, sy1, chartWidth, chartHeight);
                }
              }else if(eventIsAShot){
                //By default red color
                strokeColor = self.colorScale(0, 0, 0, 100);
                //Goal(16)
                if(event.eventTypeId === 16){
                  strokeColor = self.colorScale(1, 0, 0, 100);
                  self.drawGoalCircle(svg, chartWidth, sx1, sy1, "black", "green");
                }else{

                  let xEndShot = (100 - event.originalEndX);
                  //MissedShot(13) and shot goes over the target
                  if(event.eventTypeId === 13
                    && event.originalEndY >= 44.2 && event.originalEndY <= 55.8
                    && event.originalEndX && event.originalEndX >= 100){

                    xEndShot = -4;
                  }
                  //Update sx1 to calculate new endX (x1)
                  sx1 = x(xEndShot);
                  self.drawFailCross(svg, index, event, chartWidth, sx1, sy1, "red");

                }

                self.drawCircle(svg, index, chartWidth, sx0, sy0, previousEvent, event, nextEvent, radius(xGShotValue), opacity, strokeColor);
                self.drawShotLine(svg, index, event, sx0, sy0, sx1, sy1, radius(xGShotValue), chartWidth, strokeColor);
              }else{
                //1.- Get distance between the start and end of the event
                const distanceToPoint = self.getLengthForPoint(eventPoints[1][0], eventPoints[1][1], this);
                opacity = 1;
                //If event is unsuccessful (outcome=false), then print red (0-#fb0e0e), if not print green (1-#28e628)
                strokeColor = self.colorScale(!event.outcome ? 0 : 1, 0, 0, 100);
                //2.- Render circle event. Not shots, not passes. Not Aerial if next event is a shot
                if(!self.isAerial(event.eventTypeId) || (self.isAerial(event.eventTypeId) && !nextEventIsAShot)){
                  self.drawCircle(svg, index, chartWidth, sx0, sy0, previousEvent, event, nextEvent, radius(0.04), opacity, strokeColor);
                }
                //Distance of event line is > 40
                if(distanceToPoint && distanceToPoint > 40){
                  //3.- Draw triangle if distance between start and end of event is enough
                  const angleRadiansTraingle = Math.atan2(sy1 - sy0, sx1 - sx0);
                  sx1 -= Math.cos(angleRadiansTraingle) * radius(0.04);
                  sy1 -= Math.sin(angleRadiansTraingle) * radius(0.04);
                  self.drawPassTriangle(svg, index, event, nextEvent, sx0, sy0, sx1, sy1, chartWidth, chartHeight);
                }
              }
              //Is last event and is not a Pass
              if(event.isLastEvent && ((event.eventTypeId !== 1 && !eventIsAShot) || (event.eventTypeId === 1 && event.outcome))){ //(event.eventTypeId !== 1 || (event.eventTypeId === 1 && event.outcome))
                sx0 = x(100 - event.originalEndX);
                sy0 = y(event.originalEndY);
                sx1 = x(event.nextEventOriginalX);
                sy1 = y(100 - event.nextEventOriginalY);

                if(event.eventTypeId !== 1){
                  //If event is not a pass, update sx0 and sy0 to start dashed red line on the border of circle(event)
                  const angleRadians = Math.atan2(sy1 - sy0, sx1 - sx0);
                  sx0 = sx0 - Math.cos(angleRadians) * radius(0.02);
                  sy0 = sy0 + Math.sin(angleRadians) * (radius(0.02) * 1.5);
                }

                self.drawSimpleLine(svg, sx0, sx1, sy0, sy1, chartWidth, self.colorScale(0, 0, 0, 100), true);
                self.drawFailCross(svg, index, event, chartWidth, sx1, sy1, "red");
              }
              if(self.showSequenceNumbers){
                self.renderNextSequenceNumber(svg, chartWidth, chartHeight, event, index, radius(0.04));
              }
              if(self.showJerseyNumbers && !eventIsAShot){
                self.renderJerseyNumber(svg, chartWidth, chartHeight, event, "black", "none");
              }
            })
            .style("opacity", this.isContinuousLine(event.eventTypeId) ? 1 : 0);
      },
      dist(p1, x, y) {
        let dx = x - p1.x;
        let dy = y - p1.y;
        return Math.sqrt(dx * dx + dy * dy);
      },
      getLengthForPoint(x, y, thePath){
        let pathLength = thePath.getTotalLength();
        let precision = 100;
        let division = pathLength / precision;
        let theRecord = pathLength;
        let theSegment;

        for (let i = 0; i < precision; i++) {
          // get a point on the path for thia distance
          let _p = thePath.getPointAtLength(i * division);
          // get the distance between the new point _p and the point p
          let theDistance = this.dist(_p, x, y);
          if (theDistance < theRecord) {
          // if the distance is smaller than the record set the new record
            theRecord = theDistance;
            theSegment = i;
          }
        }
        return(theSegment * division);
      },
      getDistanceBetweenPoints(x1, x2, y1, y2){
        return Math.sqrt((x1 - x2)*(x1 - x2) + (y1 - y2)*(y1 - y2));
      },
      drawPassTriangle(svg, index, pass, nextEvent, sx0, sy0, sx1, sy1, chartWidth, chartHeight) {
        var triangleSize = chartWidth * 0.04;
        const angleRadians = Math.atan2(sy1 - sy0, sx1 - sx0);
        var verticalTransform = (angleRadians + (Math.PI / 2)) * (180/Math.PI); // To 180 and from radians to degrees
        var triangle = d3.svg.symbol()
          .type("triangle-up")
          .size(triangleSize);


        svg.append("path")
          .attr("d", triangle)
          //If pass is unsuccessful (outcome=false), then print red (0-#fb0e0e), if not print green (1-#28e628)
          .attr("stroke", this.colorScale(!pass.outcome ? 0 : 1, 0, 0, 100))
          .attr("fill", this.colorScale(!pass.outcome ? 0 : 1, 0, 0, 100))
          .attr("transform", function(d) { return "translate(" + sx1 + "," + sy1 + ") rotate(" + verticalTransform + ")"; })
          .style("opacity", 1);

      },
      //Circle is current event
      drawCircle(svg, index, chartWidth, x, y, previousEvent, circle, nextEvent, radius, fillOpacity, colorStroke) {
        var self = this;

        svg.append("circle")
          .attr("r", radius)
          .attr("cx", x)
          .attr("cy", y)
          .attr('id', "pass-" + index + "-" + circle.playerName)
          .style("fill-opacity", fillOpacity)
          .style("opacity", fillOpacity)
          .style("stroke",  colorStroke)
          .style("stroke-width", chartWidth * 0.002)
          .style("fill", colorStroke)
          .on('mouseenter',function(d) {
            d3.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr('r', radius * 1.5);

            var textLine1 = circle.teamName;
            var textLine2 = "";
            var textLine3 = circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec);

            //Missed Shot(13), ShotOnPost(14), SavedShot(15), Goal(16)
            if (self.isAshot(circle.eventTypeId)){
              //If is SavedShot(15), preposition has to be 'to' not 'of'.
              let preposition = circle.eventTypeId === 15 ? self.$t('game.gamePossessionMap.to') : self.$t('game.gamePossessionMap.of');
              textLine2 += "<b>";
              //If there is an aerial previous to the shot, then the shot is a header
              if(self.isAerial(previousEvent.eventTypeId)){
                //If is a Goal, then is a Header Goal, if not is a Header Failed
                textLine2 += circle.eventTypeId === 16 ? self.$t('game.gamePossessionMap.headerGoal') : self.$t('game.gamePossessionMap.headerFailed');
              }else{
                //Normal Shot (No header)
                textLine2 += self.$t('event.' + circle.event);
              }
              textLine2 += "</b> " + preposition + " " + circle.playerName;
              textLine3 += " · <b>" + circle.xG.toFixed(2) + " xG</b>";
            }else if (circle.eventTypeId === 1){
              //If pass is unsuccessful (outcome=false), then get failed word
              let failed = !circle.outcome ? self.$t('game.gamePossessionMap.failed') : "";
              let nextPlayerName = circle.outcome && circle.nextPlayerName ? self.$t('game.gamePossessionMap.to') + " " + circle.nextPlayerName : "";
              //Event is a Pass(1)
              textLine2 += "<b>" + self.$t('event.' + circle.event) + " " + failed + "</b>" +  " " + self.$t('game.gamePossessionMap.of') + " " + circle.playerName +
              " " + nextPlayerName;
              textLine3 += round(circle.xT, 3) > 0 ? " · <b>" + round(circle.xT, 3) + " xT</b>" : "";
            }else{
              //Rest of Events
              textLine2 += "<b>" + self.$t('event.' + circle.event) + "</b>" +  " " + self.$t('game.gamePossessionMap.of') + " " + circle.playerName;
              textLine3 += round(circle.xT, 3) > 0 ? " · <b>" + round(circle.xT, 3) + " xT</b>" : "";
              //Distance between end of current event (circle.originalEndX, circle.originalEndY) and begin of the next event (nextEvent.originalX, nextEvent.originalY)
              const distanceToNextEvent = self.getDistanceBetweenPoints(circle.originalEndX, nextEvent.originalX, circle.originalEndY, nextEvent.originalY);
              //If nextEvent is a pass(1) and there is no enough distance, add pass tooltip here
              if(nextEvent.eventTypeId === 1 && distanceToNextEvent <= 3){
                textLine2 += " " + self.$t('game.gamePossessionMap.and') + " <b>" + self.$t('event.' + nextEvent.event) + "</b> " + self.$t('game.gamePossessionMap.to') + " " + nextEvent.nextPlayerName
              }

            }

            var maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14), self.measureText(textLine3, 14));
            var widthTooltip = maxTextWidth + 60;
            var height = 70;

            var shotId = document.getElementById("pass-" + index + "-" + circle.playerName);
            var circleRadio = Math.ceil(shotId.getAttribute('r'));
            d3.select("body > .tooltip")
              .style("width", widthTooltip + "px")
              .style("height", height + "px")
              .style("left", shotId.getBoundingClientRect().left + circleRadio - widthTooltip / 2 + "px")
              .style("top", (shotId.getBoundingClientRect().top + window.scrollY + circleRadio - height - 50) + "px")
              .style("display", "inline-block")
              .style("font-size", "14px")
              .html("<b>" + textLine1 + "</b></br>" +
                    textLine2 + "</br>" +
                    textLine3 + "</br>");
          })
          .on('mouseout',function() {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('r', radius);
            d3.select("body > .tooltip").style("display", "none");
          });
      },
      drawShotLine(svg, index, shot, sx0, sy0, sx1, sy1, radius, chartWidth, stroke) {
        //Draw start of shot line on circle border instead of center of the circle
        let angle = Math.atan2(sy1 - sy0, sx1 - sx0);
        sx0 = sx0 + Math.cos(angle) * radius;
        sy0 = sy0 + Math.sin(angle) * radius;

        svg.append("line")
             .attr("x1", sx0)
             .attr("y1", sy0)
             .attr("x2", sx1)
             .attr("y2", sy1)
             .attr('id', "shot-" + index + "-" + shot.playerName)
             .attr("stroke-width", 0.002 * chartWidth)
             .attr("stroke", stroke)
             .style("opacity", 1);
      },
      drawGoalCircle(svg, pitchSize, sx1, sy1, strokeColor, fillColor){
        svg
          .append("circle")
          .attr("r", 5)
          .attr("cx", sx1)
          .attr("cy", sy1)
          .style("fill-opacity", 0.88)
          .style("opacity", 0.95)
          .style("stroke", strokeColor)
          .style("stroke-width", pitchSize * 0.0015)
          .style("fill", fillColor)
      },
      drawFailCross(svg, index, event, pitchSize, sx1, sy1, strokeColor){
        var self = this;
        svg.append('path')
        .attr("d", d3.svg.symbol().type('cross').size(pitchSize * 0.1))
        .attr('id', "event-" + (index + 1) + "-" + event.nextPlayerName)
        .attr('stroke', strokeColor)
        .attr('fill', strokeColor)
        .attr('stroke-width', 0.1)
        .attr("transform", function(d) { return "translate(" + sx1 + "," + sy1 + ") rotate(-45)"; })
        .on('mouseenter',function(d) {
          //Just allow tooltip on red cross when is not a shot
          if(!self.isAshot(event.eventTypeId)){
            d3.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer");

              var textLine1 = "";
              var textLine2 = "";
              var textLine3 = event.min + ":" + (event.sec < 10 ? "0" + event.sec : event.sec);
              var maxTextWidth = 0;
              var htmlText = "";
              var height = 50;
              //If has nextEvent and nextPlayer, print them
              if(event.nextPlayerName && event.nextEvent){
                textLine1 += "<b>" + (event.nextTeamName ? event.nextTeamName : event.teamName) + "</b>";
                textLine2 += "<b>" + self.$t('event.' + event.nextEvent) + "</b>" +  " " + self.$t('game.gamePossessionMap.of') + " " + event.nextPlayerName;
                maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14), self.measureText(textLine3, 14));
                htmlText = textLine1 + "</br>" + textLine2 + "</br>" + textLine3 + "</br>";
                height = 70;
              }else{
                //If not, just show lost of possession by teamName
                textLine1 += "<b>" + self.$t('event.lossPossession') + "</b>" +  " (" + event.teamName + ")";
                textLine2 = textLine3;
                textLine3 = "";
                maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14));
                htmlText = textLine1 + "</br>" + textLine2 + "</br>";
              }

              var widthTooltip = maxTextWidth + 60;

              var eventId = document.getElementById("event-" + (index + 1) + "-" + event.nextPlayerName);
              d3.select("body > .tooltip")
                .style("width", widthTooltip + "px")
                .style("height", height + "px")
                .style("left", eventId.getBoundingClientRect().left + 7 - widthTooltip / 2 + "px")
                .style("top", (eventId.getBoundingClientRect().top + window.scrollY - height - 40) + "px")
                .style("display", "inline-block")
                .style("font-size", "14px")
                .html(htmlText);
          }

        })
        .on('mouseout',function() {
          d3.select(this)
            .transition()
            .duration(200);
          d3.select("body > .tooltip").style("display", "none");
        });
      },
      renderNextSequenceNumber(svg, chartWidth, chartHeight, event, index, radius) {
        let sx = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
        let sy = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);
        let x = 100 - event.originalX;
        let endX = 100 - event.originalEndX;
        let y = event.originalY;
        let endY = event.originalEndY;
        let sx0 = sx(x);
        let sy0 = sy(y - 1);
        //If current event is Aerial, and next is a shot, then no print number of sequence
        //let noPrintNumber = this.isAerial(event.eventTypeId) && this.isAshot(nextEvent.eventTypeId);
        //Draw start of shot line on circle border instead of center of the circle
        let angle = Math.atan2(sy(endY) - sy(y), sx(endX) - sx(x));
        sx0 = sx0 + Math.cos(angle) * (radius * 1.5);
        //sy0 = sy0 + Math.sin(angle) * (radius * 1.5);

        //If ends on different point, set the number in the middle of segment
        if(event.originalX !== event.originalEndX){
          //Calculate the middle point of the segment
          sx0 = sx(x + (endX - x) / 2);
          sy0 = sy(y + (endY - y) / 2);
        }

        this.writeText(svg, sx0, sy0, "start", (0.016 * chartWidth) + "px", "bold", (index + 1), "white", "none");

      },
      renderJerseyNumber(svg, chartWidth, chartHeight, event, fill = "white", className = "none") {

        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);
        let radius = 7;
        let sx0 = x(100 - event.originalX);
        let sy0 = y(event.originalY);
        let sx1 = x(100 - event.originalEndX);
        let sy1 = y(event.originalEndY);
        //If current event is Aerial, and next is a shot, then no print jersey number
        //let noPrintNumber = this.isAerial(event.eventTypeId) && this.isAshot(nextEvent.eventTypeId);

        let angle2 = Math.atan2(sy1 - sy0, sx1 - sx0);
        sx1 = sx1 + Math.cos(angle2) * (radius * 2);
        sy1 = sy1 + Math.sin(angle2) * (radius * 2);

        //Draw circle
        svg.append("circle")
          .attr("r", radius)
          .attr("cx", sx1)
          .attr("cy", sy1)
          .attr('id', "jersey-" + event.nextPlayerJerseyNum)
          .style("fill-opacity", 0)
          .style("opacity", 0.8)
          .style("stroke",  fill)
          .style("stroke-width", 2 * chartWidth * 0.001);

        //Draw jersey number
        svg.append("text")
          .attr("x", sx1)
          .attr("y", sy1)
          .attr("text-anchor", "middle")
          .attr("dy", "0.3em")
          .attr("font-weight", "bold")
          .attr("class", className)
          .style("font-size", (0.012 * chartWidth) + "px")
          .style("font-family", "NeueMontreal")
          .style("fill", fill)
          .text(event.nextPlayerJerseyNum);

      },
      measureText(string, fontSize = 10) {
        const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
      		0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
      		0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
      		0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
      		0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
      		0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
      		0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
      		0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
        const avg = 0.5293256578947368;

        return string
          .split('')
          .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
          .reduce((cur, acc) => acc + cur) * fontSize;
      },
      colorScale(minDomain, maxDomain, value, steps) {
          //Range of colors in HSV format gradient
          var colorsArray = ["#fb0e0e","#fb130e","#fb180f","#fa1d0f","#fa220f","#fa270f","#fa2c10","#fa3110","#f93610","#f93b11","#f94011","#f94511","#f84911","#f84e12","#f85312",
          "#f85812","#f85d13","#f76113","#f76613","#f76b13","#f76f14","#f77414","#f67914","#f67d14","#f68215","#f68715","#f58b15","#f59016","#f59416","#f59916","#f59d16","#f4a217",
          "#f4a617","#f4aa17","#f4af17","#f4b318","#f3b818","#f3bc18","#f3c019","#f3c519","#f3c919","#f2cd19","#f2d11a","#f2d61a","#f2da1a","#f1de1a","#f1e21b","#f1e61b","#f1ea1b",
          "#f1ee1b","#eef01c","#eaf01c","#e5f01c","#e1f01d","#dcf01d","#d8ef1d","#d4ef1d","#cfef1e","#cbef1e","#c6ee1e","#c2ee1e","#beee1f","#baee1f","#b5ee1f","#b1ed1f","#aded20",
          "#a9ed20","#a4ed20","#a0ed20","#9cec21","#98ec21","#94ec21","#90ec21","#8cec22","#88eb22","#84eb22","#80eb22","#7ceb23","#78ea23","#74ea23","#70ea23","#6cea24","#68ea24",
          "#64e924","#60e924","#5ce925","#58e925","#54e925","#51e825","#4de826","#49e826","#45e826","#42e726","#3ee727","#3ae727","#37e727","#33e727","#2fe628","#2ce628","#28e628"];

          //Get scale functions.
          // - Range: Number of gradient steps
          // - Domain: Range of values
          let colorScale = d3.scale.linear().domain([minDomain, maxDomain]).range([0, steps - 1]);
          //Get index of colorsArray
          var index = colorScale(value);
          //Get whole part of the index
          var color = colorsArray[Math.trunc(index)];
          return color;
      },
      async drawImageBase64(svg, x, y, width, height, img, idName, bottom = false) {
        return new Promise((resolve) => {
          if(!this.images[idName]){
            let base_image = new Image();
            base_image.crossOrigin = "anonymous";
            base_image.src = img;
            base_image.onload = async (event) => {
              const dataUrl = await getDataUrl(event.currentTarget);
              this.drawImage(
              svg,
              x,
              y,
              width,
              height,
              dataUrl,
              idName,
              bottom
              );
              resolve(true);
            }
          }else{
            if(bottom){
              svg.node().insertBefore(this.images[idName],svg.node().firstChild);
            }else{
              svg.node().appendChild(this.images[idName]);
            }
            resolve(true);
          }
        })
      },
      drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
        let imgNode = bottom ? svg.insert("svg:image",':first-child') : svg.append("svg:image");
          imgNode.attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
        this.images[idName] = imgNode.node().cloneNode(true);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className) {
        return svg.append("rect")
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, fontWeight, text, fill = "white", className = "none") {
        svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          //.attr("dy", "0.8em")
          .attr("font-weight", fontWeight)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", "NeueMontreal")
          .style("fill", fill)
          .text(text);
      },
      isAshot(eventTypeId) {
        switch (eventTypeId) {
            //Missed Shot
            case 13:
            //Shot on Post
            case 14:
            //Saved Shot
            case 15:
            //Goal
            case 16:
              return true;
            default:
              return false;
        }
      },
      isAerial(eventTypeId) {
        switch (eventTypeId) {
            //Aerial
            case 44:
              return true;
            default:
              return false;
        }
      },
    /*  getOpacityByEventType(eventTypeId) {
        switch (eventTypeId) {
            //Pass
            case 1:
            //Clearance
            case 12:
              return 1;
            default:
              return 0;
        }
      }, */
      isContinuousLine(eventTypeId) {
        switch (eventTypeId) {
            //Pass
            case 1:
            //Clearance
            case 12:
              return true;
            default:
              return false;
        }
      },
      circleEvents(eventTypeId) {
        switch (eventTypeId) {
          //TakeOn
          case 3:
          //Tackle
          case 7:
          //Clearance
          case 12:
          //Aerial
          case 44:
          //Ball Recovery
          case 49:
            return true;
          default:
            return false;
        }
      },
      printPossessionHeader(svg, pitchSize, pitchHeight, margin, homeTeamColor, awayTeamColor, gameData, possession) {

        // =================================================================
        //      Draw Game Data (Rectangles, crests, teamNames and scores)
        // =================================================================

        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        let secondHomeTeamColor = this.homeBorderColor;
        let secondAwayTeamColor = this.awayBorderColor;

        let rectHome = this.drawRectangle(svg,
          0.045 * pitchSize + margin.left,
          margin.top - 3,
          16,
          (0.075 * pitchHeight) + 5,
          homeTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
                  rectHome
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondHomeTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondHomeTeamColor}`)
        .attr("rx",3)
        .attr("ry",3);

        const rectangleSize = 0.3 * pitchSize;
        this.drawRectangle(svg,
          (pitchSize / 2 - rectangleSize / 2) + margin.left,
          margin.top,
          rectangleSize,
          rectangleSize * 0.19,
          "black",
          0.7,
          "xgLegendRect");

        this.drawImageBase64(svg,
          0.07 * pitchSize + margin.left,
          0.03 * pitchHeight + margin.top,
          0.05 * pitchSize,
          0.05 * pitchHeight,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.homeTeamId + '.png');

        this.writeText(svg,
          0.115 * pitchSize + margin.left,
          0.07 * pitchHeight + margin.top,
          "start",
          (0.022 * pitchSize) + "px",
          500,
          `${ this.homeTeamName } • ${ gameData.homeTeamScore }`,textColor);

        let rectAway = this.drawRectangle(svg,
          0.935 * pitchSize + margin.left,
          margin.top - 3,
          16,
          (0.075 * pitchHeight) + 5,
          awayTeamColor.replace("0x", "#"), 0.9, "gameLegend1");

        rectAway
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondAwayTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondAwayTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)
        this.drawImageBase64(svg,
          0.885 * pitchSize + margin.left,
          0.03 * pitchHeight + margin.top,
          0.05 * pitchSize,
          0.05 * pitchHeight,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.awayTeamId + '.png');
        this.writeText(svg,
          0.89 * pitchSize + margin.left,
          0.07 * pitchHeight + margin.top,
          "end",
          (0.022 * pitchSize) + "px",
          500,
          `${ gameData.awayTeamScore } • ${ this.awayTeamName }`, textColor);

      // ==================================================================
      //      Draw Possession Data (Touches, passes, duration, xT and xG)
      // ==================================================================
        this.writeText(svg,
          0.4 * pitchSize + margin.left,
          0.03 * pitchHeight + margin.top,
          "middle",
          (0.016 * pitchSize) + "px",
          500,
          this.$t('game.gamePossessionMap.duration') + ": " + possession.duration + "''",
          textColor);

        this.writeText(svg,
          0.4 * pitchSize + margin.left,
          0.065 * pitchHeight + margin.top,
          "middle",
          (0.016 * pitchSize) + "px",
          500,
          this.$t('game.gamePossessionMap.passes') + ": " + possession.numPasses,
          textColor);

        this.writeText(svg,
          0.5 * pitchSize + margin.left,
          0.03 * pitchHeight + margin.top,
          "middle",
          (0.016 * pitchSize) + "px",
          500,
          this.$t('game.gamePossessionMap.touches') + ": " + possession.numTouches,
          textColor);

        this.writeText(svg,
          0.5 * pitchSize + margin.left,
          0.065 * pitchHeight + margin.top,
          "middle",
          (0.016 * pitchSize) + "px",
          500,
          this.$t('game.gamePossessionMap.time') + ": " + possession.min + ":" + possession.sec + " (" + possession.period +")",
          textColor);

        this.writeText(svg,
          0.6 * pitchSize + margin.left,
          0.03 * pitchHeight + margin.top,
          "middle",
          (0.016 * pitchSize) + "px",
          500,
          this.$t('game.gamePossessionMap.xG') + ": " + possession.xGSum, textColor);

        this.writeText(svg,
          0.6 * pitchSize + margin.left,
          0.065 * pitchHeight + margin.top,
          "middle",
          (0.016 * pitchSize) + "px",
          500,
          this.$t('game.gamePossessionMap.xT') + ": " + possession.xTSum, textColor);
      },
      printLegend(svg, chartWidth, chartHeight, margin, possession, tooltip) {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);
        let radiusScale = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);
        let radius = radiusScale(0.02);
        //Render Pass Legend
        this.drawLegendPass(svg, x(99.5), x(91), y(-3), radius, chartWidth, chartHeight, margin);
        //Render Carry and Pass Legend
        this.drawLegendCarryAndPass(svg, x(100), x(95.5), x(91), y(-7), radius, chartWidth);
        //Render Carry and Action
        this.drawLegendCarryAndAction(svg, x(70), x(61), y(-3), chartWidth);
        //Render Carry and Shot
        this.drawLegendCarryAndShot(svg, x(70), x(61), y(-7), radius, chartWidth, chartHeight, this.colorScale(1, 0, 0, 100));
        //Render Shot failed
        this.drawLegendShotFailed(svg, x(35), x(26), y(-3), radius, chartWidth, chartHeight, margin);
        //Render Goal
        this.drawLegendGoal(svg, x(35), x(26), y(-7), radius, chartWidth, chartHeight, margin);

      },
      drawLegendPass(svg, sx0, sx1, sy, radius, chartWidth, chartHeight, margin) {
        var triangleSize = chartWidth * 0.04;
        const angleRadians = Math.atan2(0, sx1 - sx0);
        var verticalTransform = (angleRadians + (Math.PI / 2)) * (180/Math.PI); // To 180 and from radians to degrees
        var triangle = d3.svg.symbol()
          .type("triangle-up")
          .size(triangleSize);

        //Print circle at the begining of pass
        svg.append("circle")
          .attr("r", radius)
          .attr("cx", sx0)
          .attr("cy", sy)
          .style("fill-opacity", 0.7)
          .style("opacity", 0.7)
          .style("stroke",  this.colorScale(1, 0, 0, 100))
          .style("stroke-width", chartWidth * 0.002)
          .style("fill", this.colorScale(1, 0, 0, 100));

        //Update sx0 to start on the border of circle
        sx0 = sx0 + Math.cos(angleRadians) * radius;

        //Print green line
    		svg.append("line")
         .attr("x1", sx0)
         .attr("y1", sy)
         .attr("x2", sx1)
         .attr("y2", sy)
         .attr("stroke-width", 0.002 * chartWidth)
         .attr("stroke", this.colorScale(1, 0, 0, 100))
         .style("opacity", 1);

       //Print green triangle
       svg.append("path")
         .attr("d", triangle)
         .attr("stroke", this.colorScale(1, 0, 0, 100))
         .attr("fill", this.colorScale(1, 0, 0, 100))
         .attr("transform", function(d) { return "translate(" + sx1 + "," + sy + ") rotate(" + verticalTransform + ")"; })
         .style("opacity", 1);

       //Print Pass text
       this.writeText(svg,
         sx1 + 10,
         sy + 4,
         "start", (0.018 * chartWidth) + "px",
         500,
         this.$t('game.gamePossessionMap.pass'),
         "white",
         "none");
      },
      drawLegendCarryAndPass(svg, sx0, sx1, sx2, sy, radius, chartWidth) {
        var triangleSize = chartWidth * 0.04;
        const angleRadians = Math.atan2(0, sx2 - sx1);
        var verticalTransform = (angleRadians + (Math.PI / 2)) * (180/Math.PI); // To 180 and from radians to degrees
        var triangle = d3.svg.symbol()
          .type("triangle-up")
          .size(triangleSize);
        //Update sx1 to finish dashed line on the border of circle
        let sx1CarryLine = sx1 - Math.cos(angleRadians) * radius;
        //Update sx1 to begin line on the border of circle
        let sx1PassLine = sx1 + Math.cos(angleRadians) * radius;

        //Print dashed green line (Carry)
        svg.append("line")
          .attr("x1", sx0)
          .attr("y1", sy)
          .attr("x2", sx1CarryLine)
          .attr("y2", sy)
          .attr("stroke-width", 0.002 * chartWidth)
          .attr("stroke", this.colorScale(1, 0, 0, 100))
          .attr("stroke-dasharray", 3)
          .style("opacity", 1);

        //Print circle at the begining of pass
        svg.append("circle")
          .attr("r", radius)
          .attr("cx", sx1)
          .attr("cy", sy)
          .style("fill-opacity", 0.7)
          .style("opacity", 0.7)
          .style("stroke",  this.colorScale(1, 0, 0, 100))
          .style("stroke-width", chartWidth * 0.002)
          .style("fill", this.colorScale(1, 0, 0, 100));

        //Print green line (Pass)
        svg.append("line")
          .attr("x1", sx1PassLine)
          .attr("y1", sy)
          .attr("x2", sx2)
          .attr("y2", sy)
          .attr("stroke-width", 0.002 * chartWidth)
          .attr("stroke", this.colorScale(1, 0, 0, 100))
          .style("opacity", 1);

        //Print green triangle
        svg.append("path")
          .attr("d", triangle)
          .attr("stroke", this.colorScale(1, 0, 0, 100))
          .attr("fill", this.colorScale(1, 0, 0, 100))
          .attr("transform", function(d) { return "translate(" + sx2 + "," + sy + ") rotate(" + verticalTransform + ")"; })
          .style("opacity", 1);

        //Print Pass text
        this.writeText(svg,
          sx2 + 10,
          sy + 4,
          "start", (0.018 * chartWidth) + "px",
          500,
          this.$t('game.gamePossessionMap.carryAndPass'),
          "white",
          "none");
      },
      drawLegendCarryAndAction(svg, sx0, sx2, sy, chartWidth) {
        //Print dashed green line (Carry)
        this.drawSimpleLine(svg, sx0, sx2, sy, sy, chartWidth, this.colorScale(1, 0, 0, 100), true);
        //Draw fill circle of the Action
        svg.append("circle")
          .attr("r", 5)
          .attr("cx", sx2)
          .attr("cy", sy)
          .style("fill-opacity", 1)
          .style("opacity", 1)
          .style("stroke",  this.colorScale(1, 0, 0, 100))
          .style("stroke-width", chartWidth * 0.002)
          .style("fill", this.colorScale(1, 0, 0, 100))

       //Print Pass text
       this.writeText(svg,
         sx2 + 10,
         sy + 4,
         "start", (0.018 * chartWidth) + "px",
         500,
         this.$t('game.gamePossessionMap.carryAndAction'),
         "white",
         "none");
      },
      drawLegendCarryAndShot(svg, sx0, sx2, sy, radius, chartWidth, chartHeight, shotColor) {
        //Update sx1 to finish dashed line on the border of circle
        const angleRadians = Math.atan2(0, sx2 - sx0);
        let sx2CarryLine = sx2 - Math.cos(angleRadians) * radius;
        //Print dashed green line (Carry)
        svg.append("line")
          .attr("x1", sx0)
          .attr("y1", sy)
          .attr("x2", sx2CarryLine)
          .attr("y2", sy)
          .attr("stroke-width", 0.002 * chartWidth)
          .attr("stroke", this.colorScale(1, 0, 0, 100))
          .attr("stroke-dasharray", 3)
          .style("opacity", 1);

        svg.append("circle")
          .attr("r", 5)
          .attr("cx", sx2)
          .attr("cy", sy)
          .style("fill-opacity", 0.7)
          .style("opacity", 0.7)
          .style("stroke",  shotColor)
          .style("stroke-width", chartWidth * 0.002)
          .style("fill", this.colorScale(1, 0, 0, 100))

       //Print Pass text
       this.writeText(svg,
         sx2 + 10,
         sy + 4,
         "start", (0.018 * chartWidth) + "px",
         500,
         this.$t('game.gamePossessionMap.carryAndShot'),
         "white",
         "none");
      },
      drawLegendShotFailed(svg, sx0, sx2, sy, radius, chartWidth, chartHeight, margin) {
        //Red color
        let strokeColor = this.colorScale(0, 0, 0, 100);
        //Update sx1 to finish dashed line on the border of circle
        const angleRadians = Math.atan2(0, sx2 - sx0);
        let sx0ShotLine = sx0 + Math.cos(angleRadians) * radius;

        svg.append("circle")
          .attr("r", 5)
          .attr("cx", sx0)
          .attr("cy", sy)
          .style("opacity", 0.7)
          .style("stroke", strokeColor)
          .style("stroke-width", chartWidth * 0.002)
          .style("fill", strokeColor);

        svg.append("line")
             .attr("x1", sx0ShotLine)
             .attr("y1", sy)
             .attr("x2", sx2)
             .attr("y2", sy)
             .attr("stroke-width", 0.002 * chartWidth)
             .attr("stroke", strokeColor)
             .style("opacity", 1);

        svg.append('path')
          .attr("d", d3.svg.symbol().type('cross').size(chartWidth * 0.1))
          .attr('stroke', strokeColor)
          .attr('fill', strokeColor)
          .attr('stroke-width', 0.1)
          .attr("transform", function(d) { return "translate(" + sx2 + "," + sy + ") rotate(-45)"; });

       //Print Pass text
       this.writeText(svg,
         sx2 + 10,
         sy + 4,
         "start", (0.018 * chartWidth) + "px",
         500,
         this.$t('game.gamePossessionMap.shotFailed'),
         "white",
         "none");
      },
      drawLegendGoal(svg, sx0, sx2, sy, radius, chartWidth, chartHeight, margin) {
        //Red color
        let strokeColor = this.colorScale(1, 0, 0, 100);
        //Update sx1 to finish dashed line on the border of circle
        const angleRadians = Math.atan2(0, sx2 - sx0);
        let sx0ShotLine = sx0 + Math.cos(angleRadians) * radius;
        let sx2ShotLine = sx2 - Math.cos(angleRadians) * radius;

        svg.append("circle")
          .attr("r", 5)
          .attr("cx", sx0)
          .attr("cy", sy)
          .style("fill-opacity", 0.7)
          .style("opacity", 0.7)
          .style("stroke", strokeColor)
          .style("stroke-width", chartWidth * 0.002)
          .style("fill", strokeColor);

        svg.append("line")
             .attr("x1", sx0ShotLine)
             .attr("y1", sy)
             .attr("x2", sx2ShotLine)
             .attr("y2", sy)
             .attr("stroke-width", 0.002 * chartWidth)
             .attr("stroke", strokeColor)
             .style("opacity", 1);

        this.drawGoalCircle(svg, chartWidth, sx2, sy, "black", "green");
       //Print Pass text
       this.writeText(svg,
         sx2 + 10,
         sy + 4,
         "start", (0.018 * chartWidth) + "px",
         500,
         this.$t('game.gamePossessionMap.goal'),
         "white",
         "none");
      },
      drawSimpleLine(svg, sx0, sx1, sy0, sy1, chartWidth, colorStroke, dashed){
        svg.append("line")
          .attr("x1", sx0)
          .attr("y1", sy0)
          .attr("x2", sx1)
          .attr("y2", sy1)
          .attr("stroke-width", 0.002 * chartWidth)
          .attr("stroke", colorStroke)
          .attr("stroke-dasharray", dashed ? 3 : 100)
          .style("opacity", 1);
      },

      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      }
    },
    destroyed() {
      delete this.observer;
    },
  }

</script>



// WEBPACK FOOTER //
// src/views/base/PossessionChart.vue